const API_BASE_URL = 'https://suttacentral.net/api';
const RAW_BILARA_BASE_URL = 'https://raw.githubusercontent.com/suttacentral/bilara-data/published';
const SITE_BASE_URL = 'https://suttacentral.net';

const COLLECTIONS = {
    dn: 'Digha Nikaya',
    mn: 'Majjhima Nikaya',
    sn: 'Samyutta Nikaya',
    an: 'Anguttara Nikaya',
    kn: 'Khuddaka Nikaya',
};

function normalizeWhitespace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
}

function uidPrefix(uid) {
    return String(uid || '').toLowerCase().match(/^[a-z]+/)?.[0] || '';
}

function rawBilaraPath(uid, author) {
    const normalizedUid = String(uid || '').toLowerCase();
    const prefix = uidPrefix(normalizedUid);
    const number = normalizedUid.match(/\d+/)?.[0];

    if (!prefix || !number) {
        throw new Error('Invalid SuttaCentral UID');
    }

    const dirs = ['sutta', prefix];
    if (prefix === 'sn' || prefix === 'an') {
        dirs.push(`${prefix}${number}`);
    }

    return `${RAW_BILARA_BASE_URL}/translation/en/${author}/${dirs.join('/')}/${normalizedUid}_translation-en-${author}.json`;
}

function buildFullText(translationText, uid) {
    const entries = Object.entries(translationText || {})
        .filter(([key, value]) => key.startsWith(`${uid}:`) && normalizeWhitespace(value))
        .filter(([key]) => !key.startsWith(`${uid}:0.`));

    const sections = new Map();
    for (const [key, value] of entries) {
        const section = key.replace(`${uid}:`, '').split('.')[0];
        if (!sections.has(section)) {
            sections.set(section, []);
        }
        sections.get(section).push(normalizeWhitespace(value));
    }

    return Array.from(sections.values())
        .map((lines) => lines.join(' '))
        .filter(Boolean)
        .join('\n\n');
}

function readingTime(text) {
    const words = String(text || '').split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

async function fetchJson(url) {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
        throw new Error(`SuttaCentral returned HTTP ${response.status}`);
    }
    return response.json();
}

async function fetchLocalProxySutra(uid, author, lang) {
    if (typeof window === 'undefined' || !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
        return null;
    }

    const params = new URLSearchParams({ author, lang });
    const response = await fetch(`/local-api/suttacentral/${encodeURIComponent(uid)}?${params.toString()}`);
    if (!response.ok) {
        return null;
    }

    const data = await response.json().catch(() => ({}));
    return data.sutra || null;
}

export async function fetchSuttaCentralCatalogDirect(options = {}) {
    const root = String(options.root || 'mn').toLowerCase();
    const lang = String(options.lang || 'en').toLowerCase();
    const translator = options.translator ? String(options.translator).toLowerCase() : null;

    const items = await fetchJson(`${API_BASE_URL}/suttaplex/${root}?lang=${lang}`);
    if (!Array.isArray(items)) return [];

    return items
        .filter((item) => {
            if (!item.uid || item.type !== 'text') return false;
            if (translator) {
                return (item.translations || []).some(
                    (t) => String(t.author_uid || '').toLowerCase() === translator && t.lang === lang
                );
            }
            return true;
        })
        .map((item) => {
            const prefix = uidPrefix(item.uid);
            const collection = COLLECTIONS[prefix] || 'SuttaCentral';
            const title = item.translated_title || item.original_title || item.uid.toUpperCase();
            return {
                uid: item.uid,
                title,
                short_title: item.acronym || item.uid.toUpperCase(),
                collection,
                blurb: item.blurb || '',
                sourceType: 'suttacentral',
                searchText: `${item.uid} ${title} ${item.acronym || ''} ${collection} ${item.blurb || ''} ${translator || ''}`.toLowerCase(),
            };
        });
}

export async function fetchSuttaCentralSutra(uid, author = 'sujato', lang = 'en') {
    const normalizedUid = String(uid || '').trim().toLowerCase();
    const normalizedAuthor = String(author || 'sujato').trim().toLowerCase();
    const normalizedLang = String(lang || 'en').trim().toLowerCase();
    if (!/^[a-z][a-z0-9.-]*$/i.test(normalizedUid)) {
        throw new Error('Enter a SuttaCentral UID, such as mn1 or sn56.11.');
    }

    const localProxySutra = await fetchLocalProxySutra(normalizedUid, normalizedAuthor, normalizedLang).catch(() => null);
    if (localProxySutra) {
        return localProxySutra;
    }

    if (normalizedLang !== 'en') {
        throw new Error('Direct browser SuttaCentral fallback currently supports English only.');
    }

    const metadataUrl = `${API_BASE_URL}/suttaplex/${normalizedUid}?lang=${normalizedLang}`;
    const translationUrl = rawBilaraPath(normalizedUid, normalizedAuthor);
    const [metadataResponse, translation] = await Promise.all([
        fetchJson(metadataUrl).catch(() => null),
        fetchJson(translationUrl),
    ]);

    const metadata = Array.isArray(metadataResponse) ? metadataResponse[0] : metadataResponse;
    const title = normalizeWhitespace(
        metadata?.translated_title ||
        translation?.[`${normalizedUid}:0.3`] ||
        translation?.[`${normalizedUid}:0.2`] ||
        normalizedUid.toUpperCase()
    );
    const fullText = buildFullText(translation, normalizedUid);
    const prefix = uidPrefix(normalizedUid);

    return {
        uuid: `suttacentral-${normalizedUid}-en-${normalizedAuthor}`,
        title,
        short_title: metadata?.acronym || normalizedUid.toUpperCase(),
        collection: COLLECTIONS[prefix] || 'SuttaCentral',
        translator: normalizedAuthor,
        estimated_reading_time: readingTime(fullText),
        full_text: fullText,
        text_link: `${SITE_BASE_URL}/${normalizedUid}/en/${normalizedAuthor}`,
        source: {
            provider: 'SuttaCentral',
            uid: normalizedUid,
            canonical_url: `${SITE_BASE_URL}/${normalizedUid}/en/${normalizedAuthor}`,
        },
        public: true,
    };
}
